"use client";

import Button from "@/components/ui/Button/Button";
import { sendGTMEvent } from "@next/third-parties/google";
import { signIn, useSession } from "next-auth/react";
import { Link, useRouter } from "@/i18n/navigation";
import { useState } from "react";
import { toast } from "react-toastify";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import Icon from "@/components/ui/Icon/Icon";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { Button as ButtonFromShad } from "@/components/ui/button";
import posthog from "posthog-js";
import { useLocale, useTranslations } from "next-intl";
import { useCommerceSettings } from "@/contexts/CommerceSettingsContext";

export default function BuyCourse(props) {
    const { data: session } = useSession();
    const router = useRouter();
    const locale = useLocale();
    const t = useTranslations();
    const [updating, setUpdating] = useState(false);
    const [open, setOpen] = useState(false);

    // Un abonné ne « commence pas gratuitement » : il paie déjà, et lui
    // promettre du gratuit sonne comme une offre qu'il a manquée. Il voit
    // « Commencer », en orange de marque, parce que c'est son action
    // principale. Même règle que le reste du site pour reconnaître un accès :
    // pro (Pro et Founder), essential (Plus), ultimate, ou une équipe.
    const hasSubscription = Boolean(
        session?.user &&
            (session.user.pro ||
                session.user.essential ||
                session.user.ultimate ||
                session.user.currentTeam),
    );

    // Formation achetée à vie : même traitement qu'un abonné SUR CETTE FICHE,
    // alors que le compte n'a pas d'abonnement. La liste vient de la session
    // (refresh-user), donc elle est connue dès que la session l'est : pas
    // d'aller-retour de plus, et le bouton de l'en-tête dit la même chose que
    // celui de la carte, sur n'importe quel navigateur et dès la première
    // visite. `props.owned` reste accepté pour les appelants qui le savent
    // déjà (la carte, juste après un achat).
    const ownsCourse = Boolean(
        props.owned ||
            (props.course?.slug &&
                session?.user?.lifetimeCourses?.includes(props.course.slug)),
    );

    // Offre gratuite fermée dans /admin/promotions : plus aucune session n'est
    // offerte, donc ce bouton ne doit plus promettre du gratuit ni inscrire
    // dans une formation qui resterait verrouillée. Il mène à l'essai.
    //
    // Tant que le réglage n'est pas connu, on garde le libellé d'avant : le cas
    // courant est une offre ouverte, et un clic passé pendant ce court instant
    // est refusé par le back, qui renvoie déjà vers la page des tarifs.
    const { settings: commerce, loaded: commerceLoaded } = useCommerceSettings();
    const trial = commerce.trial?.pricing;
    const payantSeulement =
        commerceLoaded &&
        !commerce.freeOffer.enabled &&
        !hasSubscription &&
        // Qui possède la formation n'a rien à aller chercher sur /pricing.
        !ownsCourse;

    const buyCourse = async () => {
        if (!updating) {
            if (!session) {
                router.push(
                    `/signup?courseImage=${props.course.image}&courseSlug=${props.course.slug}`,
                );
                return;
            }

            setUpdating(true);

            let response = await fetch("/api/course/post/buy", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    slug: props.course.slug,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                setUpdating(false);
                if (data.already) {
                    if (data.lastSessionProgress) {
                        router.push(
                            `/course/${props.course.slug}/${data.lastSessionProgress.partNumber}/${data.lastSessionProgress.position}`,
                        );
                    } else {
                        router.push(`/course/${props.course.slug}/1/1`);
                    }
                } else {
                    router.push({
                        pathname: "/pricing",
                        query: { checkout: "annually" },
                    });
                }
                return;
            } else {
                sendGTMEvent({
                    event: "course_started",
                });
                setUpdating(false);
                router.push(`/course/${props.course.slug}/1/1?start=1`);
                return;
            }
        }
    };
    const handleGoogleSignUp = async () => {
        if (!updating) {
            setUpdating(true);

            // gtag_report_conversion();
            let tmpUrl = `/onboarding?courseSlug=${props.course.slug}&firstTime=true`;
            if (locale !== "fr") {
                tmpUrl = `/${locale}/${tmpUrl}`;
            }

            posthog.capture("user_signup");
            // Connect user
            await signIn("google", {
                // redirect: false,
                callbackUrl: tmpUrl,
            });
        }
    };

    // Rien n'est offert en ce moment : une seule porte, l'offre d'essai. Le
    // bouton reste un bouton (router.push) plutôt qu'un lien enveloppant, un
    // <button> dans un <a> n'étant pas navigable au clavier.
    if (payantSeulement) {
        return (
            <Button
                type={
                    props.main
                        ? "primary"
                        : props.onDark
                          ? "white"
                          : "confidence"
                }
                onClick={() => router.push("/pricing")}
                className={props.onDark ? "press-safe" : undefined}
                block
            >
                {trial?.enabled
                    ? t("pricing_button_startForFreeDays", {
                          freeDays: trial.days,
                      })
                    : t("CourseBannerInfo_button")}
            </Button>
        );
    }

    return (
        <>
            {session ? (
                <Button
                    type={
                        hasSubscription || ownsCourse
                            ? "excitation"
                            : props.onDark
                              ? "white"
                              : "confidence"
                    }
                    className={props.onDark ? "press-safe" : undefined}
                    onClick={buyCourse}
                    isLoading={updating}
                    progress
                    block
                >
                    {/* {!props.main && ( */}
                    {false && (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 mr-3 text-excitationOrange"
                            viewBox="0 0 24 24"
                        >
                            <path
                                fill="currentColor"
                                d="M21.409 9.353a2.998 2.998 0 0 1 0 5.294L8.597 21.614C6.534 22.736 4 21.276 4 18.968V5.033c0-2.31 2.534-3.769 4.597-2.648l12.812 6.968Z"
                            ></path>
                        </svg>
                    )}
                    {/* label : un appelant peut imposer son libellé quand
                        « gratuitement » serait faux. */}
                    {props.label ||
                        (ownsCourse
                            ? t("course_access_owned_cta")
                            : hasSubscription
                              ? t("BuyCourse_button_member")
                              : t("BuyCourse_button"))}
                </Button>
            ) : (
                <>
                    {props.main ? (
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button block>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5 mr-3 text-white"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            fill="currentColor"
                                            d="M21.409 9.353a2.998 2.998 0 0 1 0 5.294L8.597 21.614C6.534 22.736 4 21.276 4 18.968V5.033c0-2.31 2.534-3.769 4.597-2.648l12.812 6.968Z"
                                        ></path>
                                    </svg>
                                    {t("BuyCourse_button")}
                                </Button>
                            </DialogTrigger>
                            <DialogContent
                                className="md:h-auto h-screen flex items-end bg-transparent p-0 border-none shadow-none [&>button]:hidden md:min-w-0 min-w-full"
                                onClick={(e) => {
                                    // If the click is outside the dialog
                                    if (e.target === e.currentTarget) {
                                        setOpen(false);
                                    }
                                }}
                            >
                                <DialogHeader className="bottom-0 bg-white p-5 shadow md:rounded-2xl">
                                    <div className="flex justify-center">
                                        <div className="absolute transform -translate-y-2/3 w-[130px] h-[130px] rounded-full bg-gray-100 p-10 inline-flex items-center justify-center">
                                            <Icon ratio={0.05} />
                                        </div>
                                    </div>
                                    <div>
                                        <div>
                                            <div className="uppercase text-sm text-center pt-14 opacity-80">
                                                Pour commencer
                                            </div>
                                            <DialogTitle className="text-3xl text-center mt-3">
                                                Commencez cette formation avec
                                                un compte Believemy gratuit
                                            </DialogTitle>
                                            <DialogDescription className="text-center mt-3 text-sm">
                                                {t("signup_login")}{" "}
                                                <Link
                                                    href="/login"
                                                    className="underline underline-offset-4"
                                                >
                                                    {t("signup_loginNow")}
                                                </Link>
                                            </DialogDescription>
                                        </div>

                                        <hr className="my-5" />

                                        <div className="flex flex-col gap-3">
                                            <ButtonFromShad
                                                variant="default"
                                                className="w-full flex items-center gap-1"
                                                size="lg"
                                                onClick={handleGoogleSignUp}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                                                        fill="currentColor"
                                                    />
                                                </svg>
                                                {t("signup_googleContinue")}
                                                <span className="sr-only">
                                                    {t("signup_googleContinue")}
                                                </span>
                                            </ButtonFromShad>
                                            <Link
                                                href="/signup"
                                                className="text-inherit"
                                            >
                                                <ButtonFromShad
                                                    variant="outline"
                                                    className="w-full flex items-center gap-1"
                                                    size="lg"
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="1em"
                                                        height="1em"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <g
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="1.5"
                                                            color="currentColor"
                                                        >
                                                            <path d="m2 6l6.913 3.917c2.549 1.444 3.625 1.444 6.174 0L22 6"></path>
                                                            <path d="M2.016 13.476c.065 3.065.098 4.598 1.229 5.733c1.131 1.136 2.705 1.175 5.854 1.254c1.94.05 3.862.05 5.802 0c3.149-.079 4.723-.118 5.854-1.254c1.131-1.135 1.164-2.668 1.23-5.733c.02-.986.02-1.966 0-2.952c-.066-3.065-.099-4.598-1.23-5.733c-1.131-1.136-2.705-1.175-5.854-1.254a115 115 0 0 0-5.802 0c-3.149.079-4.723.118-5.854 1.254c-1.131 1.135-1.164 2.668-1.23 5.733a69 69 0 0 0 0 2.952"></path>
                                                        </g>
                                                    </svg>
                                                    {t("signup_emailContinue")}
                                                    <span className="sr-only">
                                                        {t(
                                                            "signup_emailContinue",
                                                        )}
                                                    </span>
                                                </ButtonFromShad>
                                            </Link>
                                        </div>
                                    </div>
                                </DialogHeader>
                            </DialogContent>
                        </Dialog>
                    ) : props.onDark ? (
                        /* Variante « sur fond sombre » (panneau de fin de
                           fiche formation) : les boutons shadcn y rendaient un
                           orange sur orange invisible, et leur contour se fond
                           dans le panneau en mode sombre. On reprend le bouton
                           maison, avec une vraie hiérarchie : blanc plein pour
                           l'action la plus rapide, contour blanc pour l'autre. */
                        <div className="flex w-full flex-col gap-3">
                            <Button
                                type="white"
                                block
                                className="press-safe"
                                onClick={handleGoogleSignUp}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    className="mr-2 h-[18px] w-[18px]"
                                >
                                    <path
                                        d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                                        fill="currentColor"
                                    />
                                </svg>
                                {t("signup_googleContinue")}
                            </Button>
                            <Link href="/signup" className="text-inherit">
                                <Button type="light" block>
                                    <SolarIcon
                                        name="letter-linear"
                                        className="mr-2 text-lg"
                                    />
                                    {t("signup_emailContinue")}
                                </Button>
                            </Link>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3 w-full">
                            <ButtonFromShad
                                variant="default"
                                className="w-full flex items-center gap-1"
                                size="lg"
                                onClick={handleGoogleSignUp}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                                        fill="currentColor"
                                    />
                                </svg>
                                {t("signup_googleContinue")}
                                <span className="sr-only">
                                    {t("signup_googleContinue")}
                                </span>
                            </ButtonFromShad>
                            <Link href="/signup" className="text-inherit">
                                <ButtonFromShad
                                    variant="outline"
                                    className="w-full flex items-center gap-1"
                                    size="lg"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="1em"
                                        height="1em"
                                        viewBox="0 0 24 24"
                                    >
                                        <g
                                            fill="none"
                                            stroke="currentColor"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="1.5"
                                            color="currentColor"
                                        >
                                            <path d="m2 6l6.913 3.917c2.549 1.444 3.625 1.444 6.174 0L22 6"></path>
                                            <path d="M2.016 13.476c.065 3.065.098 4.598 1.229 5.733c1.131 1.136 2.705 1.175 5.854 1.254c1.94.05 3.862.05 5.802 0c3.149-.079 4.723-.118 5.854-1.254c1.131-1.135 1.164-2.668 1.23-5.733c.02-.986.02-1.966 0-2.952c-.066-3.065-.099-4.598-1.23-5.733c-1.131-1.136-2.705-1.175-5.854-1.254a115 115 0 0 0-5.802 0c-3.149.079-4.723.118-5.854 1.254c-1.131 1.135-1.164 2.668-1.23 5.733a69 69 0 0 0 0 2.952"></path>
                                        </g>
                                    </svg>
                                    {t("signup_emailContinue")}
                                    <span className="sr-only">
                                        {t("signup_emailContinue")}
                                    </span>
                                </ButtonFromShad>
                            </Link>
                        </div>
                    )}
                </>
            )}
        </>
    );
}
